<template>
    <div class="email-health-content">
        <div class="average-engagement-metric-container">
            <div class="progress-bar-title">
                Average engagement (days)
            </div>

            <div v-if="loading" class="label-placeholder">
                <ds-placeholder :rows="labelPlaceholder" />
            </div>

            <div v-if="!loading" class="progress-bar-threshold-labels">
                <div class="column-left">
                    0 days
                </div>

                <div class="progress-bar-upper-label">
                    {{ getAverageEngagementUpperLimit }} days (at risk)
                </div>
            </div>

            <ds-progress-bar
                :progress="getAverageEngagement"
                :max="getAverageEngagementUpperLimit"
                :class="[getEngagementColor]"
            />

            <div class="row">
                <div v-if="loading" class="value-placeholder">
                    <ds-placeholder :rows="valuePlaceholder" />
                </div>
                <div v-else class="column-left">
                    <div v-if="!hasSentMail " class="value">
                        No engagement
                    </div>

                    <div v-if="hasSentMail" class="value">
                        {{ formatAverageEngagement() }} days <ds-icon
                            v-if="!loading"
                            :name="getEngagementIndicator"
                            :class="[getEngagementColor]"
                        />
                    </div>
                </div>

                <div v-if="!loading" class="progress-bar-max-text-label">
                    <ds-icon-button
                        aria-label="Navigate to the Engagement Details page"
                        class="nav-button"
                        @click="navigateToEngagementDetails"
                    >
                        <ds-icon name="arrow-long-right" />
                    </ds-icon-button>
                </div>
            </div>
        </div>
        <div class="bounce-rate-metric-container">
            <div class="progress-bar-title">
                Invalid bounce rate
            </div>

            <div v-if="loading" class="label-placeholder">
                <ds-placeholder :rows="labelPlaceholder" />
            </div>

            <div v-else class="progress-bar-threshold-labels">
                <div class="column-left">
                    0%
                </div>

                <div class="progress-bar-upper-label">
                    {{ getBounceRateUpperLimit }}% (at risk)
                </div>
            </div>

            <ds-progress-bar
                :progress="getBounceRate"
                :max="getBounceRateUpperLimit"
                :class="[getBounceRateColor]"
            />

            <div class="row">
                <div v-if="loading" class="value-placeholder">
                    <ds-placeholder :rows="valuePlaceholder" />
                </div>

                <div v-else class="column-left">
                    <div v-if="!hasSentMail" class="value">
                        No bounces
                    </div>

                    <div v-if="hasSentMail" class="value">
                        {{ formatBounceRate() }}% <ds-icon
                            v-if="!loading"
                            :name="getBounceRateIndicator"
                            :class="[getBounceRateColor]"
                        />
                    </div>
                </div>

                <div v-if="!loading" class="progress-bar-max-text-label">
                    <ds-icon-button
                        aria-label="Bounce details"
                        class="nav-button"
                        @click="navigateToBounceDetails"
                    >
                        <ds-icon name="arrow-long-right" />
                    </ds-icon-button>
                </div>
            </div>
        </div>
        <div class="complaint-rate-metric-container">
            <div class="progress-bar-title">
                Complaint rate
            </div>

            <div v-if="loading" class="label-placeholder">
                <ds-placeholder :rows="labelPlaceholder" />
            </div>

            <div v-else class="progress-bar-threshold-labels">
                <div class="column-left">
                    0%
                </div>

                <div class="progress-bar-upper-label">
                    {{ getComplaintRateUpperLimit }}% (at risk)
                </div>
            </div>

            <ds-progress-bar
                :progress="getComplaintRate"
                :max="getComplaintRateUpperLimit"
                :class="[getComplaintColor]"
            />

            <div class="row">
                <div v-if="loading" class="value-placeholder">
                    <ds-placeholder :rows="valuePlaceholder" />
                </div>

                <div v-else class="column-left">
                    <div v-if="!hasSentMail" class="value">
                        No complaints
                    </div>

                    <div v-if="hasSentMail" class="value">
                        {{ formatComplaintRate() }}% <ds-icon
                            v-if="!loading"
                            :name="getComplaintRateIndicator"
                            :class="[getComplaintColor]"
                        />
                    </div>
                </div>

                <div v-if="!loading" class="progress-bar-max-text-label">
                    <ds-icon-button
                        aria-label="Complaint details"
                        class="nav-button"
                        @click="navigateToComplaintDetails"
                    >
                        <ds-icon name="arrow-long-right" />
                    </ds-icon-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import axios from 'axios';
import { complianceThresholds } from '@/utils/email.util';
import amplitude from '../util/analytics/amplitude';

export default {

    props: {
        isNanEmailHealthWidgetEnabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            bounceRate: null,
            averageEngagement: null,
            complaintRate: null,
            totalMailSent: null,
            uniqueRecipients: null,
            loading: true,
            labelPlaceholder: [
                { height: '16px', boxes: [] },
            ],
            valuePlaceholder: [
                { height: '24px', boxes: [] },
            ],
        };
    },

    created() {
        this.load();
    },

    computed: {
        hasSentMail() {
            if (this.totalMailSent > 0) {
                return true;
            }

            return false;
        },

        getEngagementIndicator() {
            if (this.getAverageEngagement <= this.getAverageEngagementHealthyLimit) {
                return 'thumbs-up';
            }

            if (this.getAverageEngagement > this.getAverageEngagementUpperLimit) {
                return 'alert-octagon';
            }

            return 'alert-triangle';
        },

        getBounceRateIndicator() {
            if (this.getBounceRate <= this.getBounceRateHealthyLimit) {
                return 'thumbs-up';
            }

            if (this.getBounceRate > this.getBounceRateUpperLimit) {
                return 'alert-octagon';
            }

            return 'alert-triangle';
        },

        getComplaintRateIndicator() {
            if (this.getComplaintRate <= this.getComplaintRateHealthyLimit) {
                return 'thumbs-up';
            }

            if (this.getComplaintRate > this.getComplaintRateUpperLimit) {
                return 'alert-octagon';
            }

            return 'alert-triangle';
        },

        getEngagementColor() {
            if (this.getAverageEngagement <= this.getAverageEngagementHealthyLimit) {
                return 'green';
            }

            if (this.getAverageEngagement > this.getAverageEngagementUpperLimit) {
                return 'red';
            }

            return 'yellow';
        },

        getComplaintColor() {
            if (this.getComplaintRate <= this.getComplaintRateHealthyLimit) {
                return 'green';
            }

            if (this.getComplaintRate > this.getComplaintRateUpperLimit) {
                return 'red';
            }

            return 'yellow';
        },

        getBounceRateColor() {
            if (this.getBounceRate <= this.getBounceRateHealthyLimit) {
                return 'green';
            }

            if (this.getBounceRate > this.getBounceRateUpperLimit) {
                return 'red';
            }

            return 'yellow';
        },

        getBounceRate() {
            if (this.isNanEmailHealthWidgetEnabled) {
                const bounceRate = parseFloat(this.bounceRate);

                return Number.isNaN(bounceRate) ? 0 : Math.round(bounceRate * 100) / 100;
            }

            if (this.bounceRate === null) {
                return 0;
            }

            return parseFloat(this.bounceRate).toFixed(2);
        },

        getAverageEngagement() {
            if (this.isNanEmailHealthWidgetEnabled) {
                const averageEngagement = parseFloat(this.averageEngagement);

                return Number.isNaN(averageEngagement) ? 0 : Math.round(averageEngagement * 100) / 100;
            }

            if (this.averageEngagement === null) {
                return 0;
            }

            return parseFloat(this.averageEngagement).toFixed(2);
        },

        getComplaintRate() {
            if (this.isNanEmailHealthWidgetEnabled) {
                const complaintRate = parseFloat(this.complaintRate);

                return Number.isNaN(complaintRate) ? 0 : Math.round(complaintRate * 100) / 100;
            }

            if (this.complaintRate === null) {
                return 0;
            }

            return parseFloat(this.complaintRate).toFixed(2);
        },

        getAverageEngagementUpperLimit() {
            return parseInt(complianceThresholds.averageEngagementDaysUpperLimit, 10);
        },

        getAverageEngagementHealthyLimit() {
            return parseInt(complianceThresholds.averageEngagementDaysHealthyLimit, 10);
        },

        getBounceRateUpperLimit() {
            return parseFloat(complianceThresholds.bounceRateUpperLimit);
        },

        getBounceRateHealthyLimit() {
            return parseFloat(complianceThresholds.bounceRateHealthyLimit);
        },

        getComplaintRateUpperLimit() {
            return parseFloat(complianceThresholds.complaintRateUpperLimit);
        },

        getComplaintRateHealthyLimit() {
            return parseFloat(complianceThresholds.complaintRateHealthyLimit);
        },
    },

    methods: {
        async load() {
            this.loadMetrics();
        },

        async loadMetrics() {
            try {
                const metricsResponse = await axios.get('/app/metrics/getEmailHealthMetrics');

                this.bounceRate = metricsResponse.data.bounceRate;
                this.averageEngagement = metricsResponse.data.averageEngagement;
                this.complaintRate = metricsResponse.data.complaintRate;
                this.totalMailSent = metricsResponse.data.totalMailSent;
                this.uniqueRecipients = metricsResponse.data.uniqueRecipients;
            } catch (e) {
                this.bounceRate = null;
                this.averageEngagement = null;
                this.complaintRate = null;
                this.uniqueRecipients = null;
                this.totalMailSent = null;
            }

            this.loading = false;
        },

        formatBounceRate() {
            const bounceRate = this.getBounceRate;

            return this.isNanEmailHealthWidgetEnabled ? Number(bounceRate).toFixed(2) : bounceRate;
        },

        formatComplaintRate() {
            const complaintRate = this.getComplaintRate;

            return this.isNanEmailHealthWidgetEnabled ? Number(complaintRate).toFixed(2) : complaintRate;
        },

        formatAverageEngagement() {
            const engagement = this.getAverageEngagement;

            return this.isNanEmailHealthWidgetEnabled ? Number(engagement) : engagement;
        },

        navigateToBounceDetails() {
            this.emitAmplitudeEvent('bounce');
            this.navigateOutOfIFrame('/app/emailHealth/bounceDetails');
        },

        navigateToComplaintDetails() {
            this.emitAmplitudeEvent('complaints');
            this.navigateOutOfIFrame('/app/emailHealth/complaintDetails');
        },

        navigateToEngagementDetails() {
            this.emitAmplitudeEvent('engagement');
            this.navigateOutOfIFrame('/app/emailHealth/engagementDetails');
        },

        navigateOutOfIFrame(address) {
            window.top.location.href = address;
        },

        emitAmplitudeEvent(event) {
            amplitude.infusionsoft.track('Email Analytics', {
                'Event Source': 'Home Page Email Health Widget',
                Destination: event,
            });
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
 // Fixed px sizing (not DS rem tokens) intentionally neutralizes browser Font Size setting — see CUS-13043.
 $fixed-spacing-sm: 4px;
 $fixed-spacing-md: 8px;
 $fixed-spacing-lg: 12px;
 $fixed-font-size-sm: 14px;
 $fixed-font-size-xl: 24px;
 $fixed-icon-size: 24px;

 .email-health-content {
     margin-left: -$fixed-spacing-md;
     margin-right: -$fixed-spacing-md;

     .progress-bar {
         margin-bottom: 0;
     }

     :deep(.nav-button) {
         --button-padding-top: 7px;
         --button-padding-right: 7px;
         --button-padding-bottom: 7px;
         --button-padding-left: 7px;
         --icon-size: #{$fixed-icon-size};
         font-size: 20px;
         line-height: 16px;

         &.icon-only {
             padding: 7px;
         }
     }

     :deep(.progress-bar .bar-container) {
         height: $fixed-spacing-md;
     }
     :deep(.progress-bar .info) {
         margin-top: $fixed-spacing-sm;
     }
 }

 .average-engagement-metric-container {
     border-bottom: solid 1px $color-ink-200;
     border-top: solid 1px $color-ink-200;
     padding-top: $fixed-spacing-md;
     padding-left: $fixed-spacing-md;
     padding-right: $fixed-spacing-md;
 }

 .bounce-rate-metric-container {
     margin-top: $fixed-spacing-md;
     border-bottom: solid 1px $color-ink-200;
     padding-left: $fixed-spacing-md;
     padding-right: $fixed-spacing-md;
 }

 .complaint-rate-metric-container {
     margin-top: $fixed-spacing-md;
     padding-left: $fixed-spacing-md;
     padding-right: $fixed-spacing-md;
     padding-bottom: $fixed-spacing-md;
 }

 .yellow {
     --progress-bar-color: #{$color-yellow};
     --icon-color: #{$color-yellow};
     margin-bottom: 0;
 }

 .value {
     font-size: $fixed-font-size-xl;
     margin-bottom: $fixed-spacing-md;
     float:left;

     :deep(.icon) {
         --icon-size: 24px;
     }
 }

 .green {
     --progress-bar-color: #{$color-green};
     --icon-color: #{$color-green};
     margin-bottom: 0;
 }

 .red {
     --progress-bar-color: #{$color-red-800};
     --icon-color: #{$color-red-500};
     margin-bottom: 0;
 }

 .row {

 }

 .progress-bar-title {
     font-size: $fixed-font-size-sm;
     color: $color-ink-900;
     padding-bottom: $fixed-spacing-sm;
 }

 .progress-bar-threshold-labels {
     font-size: $fixed-font-size-sm;
     color: $color-ink-800;
     padding-bottom: $fixed-spacing-sm;
 }

 .column-left {
     float: left;
 }

 .coming-soon-label {
     color: $color-green;
 }

 .progress-bar-max-text-label {
     text-align: right;
     color: $color-ink-800;
 }

 .progress-bar-upper-label {
     flex: 50%;
     text-align: right;
     color: $color-ink-800;
 }

 .label-placeholder {
     width: 100%;
 }

 .value-placeholder {
     margin-bottom: $fixed-spacing-lg;
     width: 50%;
 }
</style>
